import React, { useEffect, useMemo, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { renderInlineBold } from "../../utils/text/renderInlineBold";
import { advertisingActive } from "../../utils/trackerGating";
import { consentHeaders } from "../../utils/consent";

const DEFAULT_COPY = {
  header: "This Brief Isn’t Public Yet",
  bodyParagraphs: [
    "The [City] Brief is being released to a small first group to validate demand before public launch.",
    "We’re confirming interest from people who would actually pay for this kind of local intelligence.",
  ],
  priceAnchor: "Expected price: $29",
  emailPlaceholder: "Email address",
  ctaLabel: "Request First Access",
  microcopy: "No spam. No promises. This is a signal test, not a subscription.",
  successHeader: "You’re in the First Group",
  successBodyParagraphs: [
    "When access opens, you’ll be able to purchase the [City] Brief before it’s shared more widely.",
    "If demand is strong, this becomes a recurring local series.",
    "If not, it stops here.",
  ],
};

function applyCityTokens(text, cityLabel) {
  if (!text || typeof text !== "string") return text;
  const city = cityLabel || "San Antonio";
  return text
    .replaceAll("[City]", city)
    .replaceAll("[CITY]", city.toUpperCase());
}

function renderCopyLine(text, cityLabel) {
  const withTokens = applyCityTokens(text, cityLabel);
  return renderInlineBold(withTokens);
}

export default function BriefEarlyAccessInterstitial({
  open,
  submitUrl,
  cityLabel,
  content,
  visualVariant = "default",
  extraPayload,
  onRequestClose,
  closeLabel = "Back to results",
  trackingFlow = "brief_early_access",
  trackingCity,
  trackingCitySlug = "sanantonio",
  trackingPersona = "customer_service_rep",
  previewOnly = false,
  postSubmitPicker = null,
}) {
  const copy = useMemo(
    () => ({ ...DEFAULT_COPY, ...(content || {}) }),
    [content]
  );
  const variant = (copy?.variant || "email").toLowerCase();

  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [err, setErr] = useState(false);
  const [emailErr, setEmailErr] = useState(false);
  const [pickedTopics, setPickedTopics] = useState([]);
  const [pickerDone, setPickerDone] = useState(false);
  const recaptchaRef = useRef(null);

  // Scroll lock + freeze background page
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    const prevTouchAction = document.body.style.touchAction;
    document.body.classList.add("tbb-interstitial-open");
    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.touchAction = prevTouchAction;
      document.body.classList.remove("tbb-interstitial-open");
    };
  }, [open]);

  // Reset state when modal closes
  useEffect(() => {
    if (open) return;
    setEmail("");
    setSubmitted(false);
    setErr(false);
    setEmailErr(false);
    setIsSubmitting(false);
    setPickedTopics([]);
    setPickerDone(false);
    recaptchaRef.current?.reset?.();
  }, [open]);

  const validateEmail = (value) => {
    return String(value)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );
  };

  const handleSubmitEmail = async (e) => {
    e.preventDefault();
    if (submitted || isSubmitting) return;
    if (previewOnly) return;

    const trimmed = email.trim();
    if (!trimmed) {
      setErr(true);
      return;
    }

    if (!validateEmail(trimmed)) {
      setEmailErr(true);
      return;
    }

    setErr(false);
    setEmailErr(false);
    setIsSubmitting(true);

    try {
      let captchaToken = null;
      try {
        const tokenPromise = recaptchaRef.current?.executeAsync?.();
        if (tokenPromise) {
          captchaToken = await Promise.race([
            tokenPromise,
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error("Captcha timeout")), 5000)
            ),
          ]);
        }
      } catch (e1) {
        console.warn("ReCAPTCHA execution failed or timed out:", e1);
      }

      recaptchaRef.current?.reset?.();

      // captcha is required on backend; if token missing, treat as failure
      if (!captchaToken) throw new Error("Missing captcha token");

      const url = submitUrl || "/api/users/local-report-early";
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: trimmed,
          captchaToken,
          city: trackingCitySlug,
          persona: trackingPersona,
          ...(extraPayload && typeof extraPayload === "object" ? extraPayload : {}),
        }),
      });

      if (!res.ok) throw new Error(`Request failed: ${res.status}`);

      setSubmitted(true);
      // Funnel step 2: successful early-access request submission
      // (No PII in params; keep this lightweight for reporting.)
      const eventId = crypto.randomUUID();
      const customData = {
        stage: "form_submit",
        flow: trackingFlow,
        city: trackingCity || undefined,
        city_slug: trackingCitySlug,
        persona: trackingPersona,
      };

      // Advertising events only run with advertising consent. The browser pixel and
      // the server-side CAPI call are gated together, so a visitor who rejected is
      // not simply reported to Meta from the server instead.
      if (advertisingActive()) {
        window.fbq("track", "Subscribe", customData, { eventID: eventId });

        // Send the same event_id server-side for deduplication (CAPI).
        // Fire-and-forget to keep UX fast.
        fetch("/api/meta/capi", {
          method: "POST",
          headers: { "Content-Type": "application/json", ...consentHeaders() },
          keepalive: true,
          body: JSON.stringify({
            event_name: "Subscribe",
            event_id: eventId,
            custom_data: customData,
          }),
        }).catch(() => {});
      }
    } catch (e2) {
      setErr(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  const headerText = submitted ? copy.successHeader : copy.header;
  const body = submitted ? copy.successBodyParagraphs : copy.bodyParagraphs;
  const rootClassName = [
    "tbb-interstitial",
    visualVariant === "results" ? "tbb-interstitial--results" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={rootClassName} role="dialog" aria-modal="true">
      <div className="tbb-interstitial__wrap" aria-label={headerText}>
        <div className="tbb-interstitial__inner">
          {typeof onRequestClose === "function" ? (
            <button
              type="button"
              className="tbb-interstitial__close"
              onClick={onRequestClose}
            >
              {closeLabel}
            </button>
          ) : null}

          <h1 className="tbb-interstitial__title">
            {renderCopyLine(headerText, cityLabel)}
          </h1>

          <div className="tbb-interstitial__body">
            {(body || []).filter(Boolean).map((p, idx) => (
              <p key={idx} className="tbb-interstitial__p">
                {renderCopyLine(p, cityLabel)}
              </p>
            ))}
          </div>

          {submitted && postSubmitPicker && !pickerDone ? (
            <div className="tbb-interstitial__picker">
              <p className="tbb-interstitial__picker-title">
                {postSubmitPicker.title || "What do you most want covered?"}
              </p>
              <ul className="tbb-interstitial__picker-options">
                {(postSubmitPicker.options || []).map((opt) => {
                  const checked = pickedTopics.includes(opt.id);
                  return (
                    <li key={opt.id}>
                      <label className="tbb-interstitial__picker-option">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() =>
                            setPickedTopics((prev) =>
                              prev.includes(opt.id)
                                ? prev.filter((id) => id !== opt.id)
                                : [...prev, opt.id],
                            )
                          }
                        />
                        <span>{opt.label}</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
              <button
                type="button"
                className="tbb-interstitial__button"
                disabled={pickedTopics.length === 0}
                onClick={() => {
                  if (typeof postSubmitPicker.onConfirm === "function") {
                    postSubmitPicker.onConfirm(pickedTopics);
                  }
                  setPickerDone(true);
                }}
              >
                {postSubmitPicker.confirmLabel || "Save my picks"}
              </button>
              <button
                type="button"
                className="tbb-interstitial__picker-skip"
                onClick={() => setPickerDone(true)}
              >
                {postSubmitPicker.skipLabel || "Skip for now"}
              </button>
            </div>
          ) : null}

          {variant === "email" ? (
            !submitted ? (
              <form className="tbb-interstitial__form" onSubmit={handleSubmitEmail}>
                {previewOnly ? (
                  <div className="tbb-interstitial__microcopy" style={{ marginBottom: "12px" }}>
                    Admin preview only. Email submission is disabled here.
                  </div>
                ) : null}
                {err ? (
                  <div className="tbb-interstitial__error">
                    Error... please try again later
                  </div>
                ) : null}

                <input
                  className="tbb-interstitial__input"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder={copy.emailPlaceholder || "Email address"}
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailErr) setEmailErr(false);
                  }}
                  disabled={submitted || isSubmitting || previewOnly}
                  aria-label="Email address"
                  required
                />

                {emailErr ? (
                  <div
                    className="tbb-interstitial__error"
                    style={{ marginTop: "-8px", marginBottom: "12px" }}
                  >
                    Invalid email address
                  </div>
                ) : null}

                <button
                  type="submit"
                  className="tbb-interstitial__button"
                  disabled={submitted || isSubmitting || previewOnly}
                >
                  {copy.ctaLabel || "Request First Access"}
                </button>

                <div className="tbb-interstitial__microcopy">
                  {previewOnly
                    ? "Preview mode shows the real modal layout, but submission stays disabled."
                    : renderCopyLine(copy.microcopy, cityLabel)}
                </div>
              </form>
            ) : null
          ) : null}
        </div>
      </div>

      {variant === "email" && !submitted && !previewOnly && (
        <div
          style={{
            position: "absolute",
            bottom: 0,
            right: 0,
            pointerEvents: "none",
          }}
        >
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={"6LcUkYkoAAAAADH7KJoPEM1bYaEqkipI8wBlPCLU"}
            size="invisible"
          />
        </div>
      )}

      {/* no outside click close, no ESC close, no X (per spec) */}
    </div>
  );
}
